import React from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from "@mui/material";
import { GRUPOS_PERMISOS, TODOS_LOS_PERMISOS } from "./permisos";

const contar = (permisos, lista) => lista.filter((d) => permisos[d]).length;

/**
 * Casillas de permisos agrupadas por módulo. Cada casilla es el `isEnable` de su fila; "Marcar
 * todos" de un grupo solo toca ese grupo. Lo global ("Marcar todo" / "Quitar todo") lo confirma
 * quien usa el editor, con `onTodo`.
 *
 * @param {object} props
 * @param {Object<string, boolean>} props.permisos
 * @param {Function} props.onCambiar (permisosNuevos) => void
 * @param {Function} [props.onTodo] (valor: boolean) => void; si falta no se muestran los botones globales.
 * @param {boolean} [props.deshabilitado]
 */
const EditorPermisos = ({ permisos, onCambiar, onTodo, deshabilitado = false }) => {
  const total = contar(permisos, TODOS_LOS_PERMISOS);

  const cambiar = (description) =>
    onCambiar({ ...permisos, [description]: !permisos[description] });

  const ponerGrupo = (grupo, valor) =>
    onCambiar({
      ...permisos,
      ...Object.fromEntries(grupo.permisos.map((p) => [p.description, valor])),
    });

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Typography variant="body2" sx={{ color: "text.secondary" }} aria-live="polite">
          {`${total} de ${TODOS_LOS_PERMISOS.length} permisos marcados`}
        </Typography>
        {onTodo ? (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onTodo(true)}
              disabled={deshabilitado || total === TODOS_LOS_PERMISOS.length}
            >
              Marcar todo
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => onTodo(false)}
              disabled={deshabilitado || total === 0}
            >
              Quitar todo
            </Button>
          </Stack>
        ) : null}
      </Stack>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" },
          alignItems: "start",
        }}
      >
        {GRUPOS_PERMISOS.map((grupo) => {
          const lista = grupo.permisos.map((p) => p.description);
          const marcados = contar(permisos, lista);
          const todos = marcados === lista.length;
          const idTitulo = `grupo-permisos-${grupo.titulo.replace(/\s+/g, "-")}`;
          return (
            <Box
              key={grupo.titulo}
              component="fieldset"
              aria-labelledby={idTitulo}
              sx={{
                m: 0,
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2.5,
                minWidth: 0,
              }}
            >
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography id={idTitulo} component="h3" variant="subtitle1" sx={{ fontWeight: 700, color: "primary.main", p: 0 }}>
                    {grupo.titulo}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {`${marcados} de ${lista.length}`}
                    {grupo.descripcion ? ` · ${grupo.descripcion}` : ""}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  onClick={() => ponerGrupo(grupo, !todos)}
                  disabled={deshabilitado}
                  aria-label={`${todos ? "Quitar" : "Marcar"} todos los permisos de ${grupo.titulo}`}
                  sx={{ flexShrink: 0 }}
                >
                  {todos ? "Quitar todos" : "Marcar todos"}
                </Button>
              </Stack>
              <FormGroup sx={{ mt: 1 }}>
                {grupo.permisos.map((p) => (
                  <FormControlLabel
                    key={p.description}
                    disabled={deshabilitado}
                    control={
                      <Checkbox
                        checked={Boolean(permisos[p.description])}
                        onChange={() => cambiar(p.description)}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="body2">{p.label}</Typography>
                        {p.ayuda ? (
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                            {p.ayuda}
                          </Typography>
                        ) : null}
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", mr: 0, "& .MuiCheckbox-root": { pt: 0.75 } }}
                  />
                ))}
              </FormGroup>
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
};

export default EditorPermisos;
